<template>
    <div class="pairing-page">
        <div class="page-toolbar">
            <el-button size="small" icon="el-icon-setting" class="category-btn" @click="$refs.categoryManager.open()">分类管理</el-button>
            <el-button size="small" icon="el-icon-document" @click="$refs.textManager.open()">文案管理</el-button>
            <el-button size="small" icon="el-icon-notebook-2" @click="$refs.dictManager.open()">字典管理</el-button>
            <el-button size="small" icon="el-icon-view" @click="openPreview">蓝牙表预览</el-button>
        </div>
        <div class="pairing-layout">
            <!-- 左侧：配网型号 -->
            <div class="model-panel">
                <div class="panel-toolbar">
                    <div class="toolbar-line">
                        <el-select v-model="category" size="small" clearable placeholder="全部分类" style="width: 130px;"
                            @change="loadModels">
                            <el-option v-for="c in categories" :key="c.id" :label="c.name" :value="c.id"></el-option>
                        </el-select>
                        <el-input v-model.trim="kname" size="small" clearable placeholder="搜索配网型号"
                            @keyup.enter="searchModels" @blur="searchModels" @clear="searchModels"></el-input>
                        <el-button type="primary" size="small" icon="el-icon-plus" @click="openModelDialog()"></el-button>
                    </div>
                    <div class="toolbar-line">
                        <el-select v-model="appFilter" size="small" clearable placeholder="全部 App" style="width: 100%;"
                            @change="onAppFilterChange">
                            <el-option v-for="a in meta.apps || []" :key="a.code" :label="a.name" :value="a.code"></el-option>
                        </el-select>
                    </div>
                </div>
                <div class="sort-tip">{{ sortTip }}</div>
                <div class="model-list" v-loading="modelLoading">
                    <!-- 按分类分组展示，与 App 一致；只能在同一分类内拖动 -->
                    <div v-for="group in modelGroups" :key="group.id" class="model-group">
                        <div class="group-title">{{ group.name }}（{{ group.models.length }}）</div>
                        <div v-for="(m, index) in group.models" :key="m.code" class="model-item"
                            :class="[{ active: current && current.code === m.code, sortable: canSortModels }, dragClass(index, group.id)]"
                            :draggable="canSortModels ? 'true' : 'false'" @click="selectModel(m)"
                            @dragstart="canSortModels && onDragStart(index, $event, group.id)"
                            @dragover.prevent="onDragOver(index, group.id)"
                            @drop.prevent="onDrop(group.models, sortModels)" @dragend="onDragEnd">
                            <div class="model-main">
                                <span class="model-name">{{ m.model }}</span>
                                <el-tag v-if="m.test_only" size="mini" type="warning">仅测试</el-tag>
                                <el-tag v-if="m.ble_table" size="mini">蓝牙表</el-tag>
                            </div>
                            <div class="model-sub">
                                {{ m.scheme_count }} 个方案 · {{ m.product_model_count }} 个具体型号
                            </div>
                            <div v-if="appNames(m).length" class="model-apps">
                                <el-tag v-for="name in appNames(m)" :key="name" size="mini" type="info">{{ name }}</el-tag>
                            </div>
                            <div v-else class="model-apps model-apps-all">全部 App</div>
                        </div>
                    </div>
                    <div v-if="!models.length && !modelLoading" class="empty">暂无配网型号</div>
                </div>
            </div>

            <!-- 右侧：配网方案 -->
            <div class="scheme-panel">
                <template v-if="current">
                    <div class="scheme-header">
                        <div>
                            <span class="scheme-title">{{ current.model }}</span>
                            <span class="scheme-meta">{{ categoryName(current.category) }} · 蓝牙 modelId：{{ current.model_id }}</span>
                        </div>
                        <div>
                            <el-button size="small" @click="openModelDialog(current)">编辑型号</el-button>
                            <el-button size="small" type="danger" plain @click="deleteModel(current)">删除型号</el-button>
                            <el-button size="small" type="primary" icon="el-icon-plus"
                                @click="$refs.editor.open(current)">新增方案</el-button>
                        </div>
                    </div>
                    <div class="sort-tip">拖动行调整方案顺序，同一型号有多个方案时 App 按此顺序展示</div>
                    <el-table ref="schemeTable" :data="schemes" border size="small" row-key="code"
                        v-loading="schemeLoading" :row-class-name="schemeRowClass">
                        <el-table-column width="40" align="center">
                            <template #default>
                                <i class="el-icon-rank drag-handle"></i>
                            </template>
                        </el-table-column>
                        <el-table-column label="列表图" width="70" align="center">
                            <template v-slot="scope">
                                <el-image v-if="imageOf(scope.row)" :src="imageOf(scope.row)" fit="contain"
                                    class="scheme-thumb"></el-image>
                            </template>
                        </el-table-column>
                        <el-table-column label="方案名称" prop="name" min-width="180" show-overflow-tooltip></el-table-column>
                        <el-table-column label="蓝牙 modelId" width="100" align="center">
                            <template v-slot="scope">
                                <span v-if="scope.row.functions.includes('support_ble') && !scope.row.ble_match" class="form-tip">不匹配</span>
                                <span v-else>{{ modelIdOf(scope.row) || '-' }}</span>
                            </template>
                        </el-table-column>
                        <el-table-column label="配网方式" min-width="200">
                            <template v-slot="scope">
                                <el-tag v-for="f in functionsOf(scope.row, 'connection')" :key="f.code" size="mini"
                                    class="fn-tag">{{ f.name }}</el-tag>
                            </template>
                        </el-table-column>
                        <el-table-column label="设备特性" min-width="200">
                            <template v-slot="scope">
                                <el-tag v-for="f in functionsOf(scope.row, 'feature')" :key="f.code" size="mini" type="info"
                                    class="fn-tag">{{ f.name }}</el-tag>
                            </template>
                        </el-table-column>
                        <el-table-column label="展示" width="110" align="center">
                            <template v-slot="scope">
                                <el-tag v-if="scope.row.test_only" size="mini" type="warning">仅测试</el-tag>
                                <el-tag v-if="scope.row.hide_in_list" size="mini" type="info">隐藏</el-tag>
                                <span v-if="!scope.row.test_only && !scope.row.hide_in_list" class="form-tip">所有用户可见</span>
                            </template>
                        </el-table-column>
                        <el-table-column label="操作" width="150" align="center">
                            <template v-slot="scope">
                                <el-button type="text" size="small" @click="$refs.editor.open(current, scope.row)">编辑</el-button>
                                <el-button type="text" size="small" @click="copyScheme(scope.row)">复制</el-button>
                                <el-button type="text" size="small" style="color: #f56c6c;"
                                    @click="deleteScheme(scope.row)">删除</el-button>
                            </template>
                        </el-table-column>
                    </el-table>
                </template>
                <div v-else class="empty">请选择左侧配网型号</div>
            </div>
        </div>

        <el-dialog :title="modelForm.code ? '编辑配网型号' : '新增配网型号'" v-model="modelDialogVisible" width="560px">
            <el-form ref="modelFormRef" :model="modelForm" :rules="modelRules" label-width="110px" size="small">
                <el-form-item label="分类" prop="category">
                    <el-select v-model="modelForm.category" style="width: 100%;">
                        <el-option v-for="c in formCategories" :key="c.id" :label="c.name" :value="c.id"></el-option>
                    </el-select>
                </el-form-item>
                <el-form-item label="配网型号" prop="model">
                    <el-input v-model="modelForm.model" :disabled="modelNameLocked"
                        placeholder="如 GC2，具体型号 GC2_A 等归属于它"></el-input>
                    <div v-if="modelNameLocked" class="form-tip">已有配网方案，型号名决定蓝牙 modelId，不能修改</div>
                </el-form-item>
                <el-form-item label="蓝牙 modelId">
                    <span class="model-id">{{ crc(modelForm.model) || '-' }}</span>
                    <span class="form-tip">由配网型号计算 CRC16，仅蓝牙配网方案使用</span>
                </el-form-item>
                <el-form-item label="展示名">
                    <el-input v-model="modelForm.name" :placeholder="`留空等于配网型号${modelForm.model ? '：' + modelForm.model : ''}`"></el-input>
                    <div class="form-tip">首页蓝牙发现设备时显示的名称</div>
                </el-form-item>
                <el-form-item label="配网图">
                    <div class="pair-image-row">
                        <el-input v-model.trim="modelForm.pair_image" clearable placeholder="图片地址，正方形"></el-input>
                        <el-image v-if="modelForm.pair_image" :src="modelForm.pair_image" fit="contain"
                            class="pair-image-thumb" :preview-src-list="[modelForm.pair_image]"></el-image>
                    </div>
                    <div class="form-tip">首页蓝牙发现设备、免费云存等处的设备图；具体型号未单独设置时也用它。留空时使用第一个配网方案的产品主图</div>
                </el-form-item>
                <el-form-item label="下发到蓝牙表">
                    <el-switch v-model="modelForm.ble_table"></el-switch>
                    <div class="form-tip">开启后 App 首页可通过蓝牙广播发现该型号设备，同时需要有蓝牙配网方案才能完成配网</div>
                </el-form-item>
                <el-form-item label="适用 App">
                    <el-select v-model="modelForm.apps" multiple placeholder="留空表示所有 App 可用" style="width: 100%;">
                        <el-option v-for="a in meta.apps || []" :key="a.code" :label="a.name" :value="a.code"></el-option>
                    </el-select>
                    <div class="form-tip">留空表示所有 App 可用；只对 App 接口生效，不影响后台管理</div>
                </el-form-item>
            </el-form>
            <template #footer>
                <el-button size="small" @click="modelDialogVisible = false">取消</el-button>
                <el-button type="primary" size="small" @click="saveModel">保存</el-button>
            </template>
        </el-dialog>

        <el-dialog :title="`蓝牙表预览（${previewCount} 条）`" v-model="previewVisible" width="900px">
            <div class="form-tip" style="margin-bottom: 8px;">按当前配网型号生成，结构与原 osaio_IPCBlueToothTable.json 一致；只包含开启了「下发到蓝牙表」的配网型号</div>
            <pre class="preview-json">{{ previewData }}</pre>
        </el-dialog>

        <SchemeEditor ref="editor" :meta="meta" @saved="onSchemeSaved" @manage-texts="openTextManager"></SchemeEditor>
        <CategoryManager ref="categoryManager" :meta="meta" :default-app="appFilter" @changed="onCategoryChanged"
            @manage-texts="openTextManager"></CategoryManager>
        <TextManager ref="textManager" :meta="meta" @changed="onTextChanged"></TextManager>
        <DictManager ref="dictManager" @changed="loadMeta"></DictManager>
    </div>
</template>

<script>
import pairingApi from './api'
import { crcModelId } from './utils'
import SchemeEditor from './SchemeEditor.vue'
import CategoryManager from './CategoryManager.vue'
import TextManager from './TextManager.vue'
import DictManager from './DictManager.vue'
import { dragSortMixin } from './dragSort'

export default {
    name: 'PairingConfig',
    components: { SchemeEditor, CategoryManager, TextManager, DictManager },
    mixins: [dragSortMixin],
    data() {
        return {
            meta: { functions: [], led_effects: [], led_status_presets: [], text_types: [], languages: [], reset_colors: [], image_types: [], capability_fields: [], apps: [], default_app: '' },
            categories: [],
            // 全量分类：按 App 筛选时 categories 只有该 App 的，表单里的分类下拉仍要用全部
            allCategories: [],
            previewVisible: false,
            previewData: '',
            previewCount: 0,
            category: '',
            appFilter: '',
            kname: '',
            models: [],
            modelLoading: false,
            current: null,
            schemes: [],
            schemeLoading: false,
            modelDialogVisible: false,
            modelForm: {},
            modelRules: {
                category: [{ required: true, message: '请选择分类', trigger: 'change' }],
                model: [
                    { required: true, message: '请输入配网型号', trigger: 'blur' },
                    { pattern: /^\S(.*\S)?$/, message: '首尾不能有空格', trigger: 'blur' },
                ],
            },
            schemeDrag: { from: -1, over: -1 },
            lastKname: '',
        }
    },
    computed: {
        // 已有方案时型号名决定蓝牙 modelId，不能改
        modelNameLocked() {
            return !!this.modelForm.code && this.modelForm.scheme_count > 0
        },
        // 排序按 App 独立，只有选定具体 App 时才能拖动；搜索时列表被过滤，也不能拖
        canSortModels() {
            return !!this.appFilter && !this.kname
        },
        sortTip() {
            if (!this.appFilter) return '排序按 App 独立：先在上方选择 App，再拖动调整该 App 下的显示顺序'
            if (this.kname) return '清空搜索后可拖动排序'
            return '按住型号拖动，调整「' + this.appName(this.appFilter) + '」下的显示顺序'
        },
        // 型号表单里的分类下拉：按 App 筛选时也要能选到其它分类
        formCategories() {
            return this.allCategories.length ? this.allCategories : this.categories
        },
        modelGroups() {
            return this.categories
                .map((c) => ({ id: c.id, name: c.name, models: this.models.filter((m) => m.category === c.id) }))
                .filter((g) => g.models.length)
        },
    },
    created() {
        this.loadMeta()
        this.loadCategories()
        this.loadModels()
    },
    methods: {
        // App code → 显示名
        appName(code) {
            const app = (this.meta.apps || []).find((a) => a.code === code)
            return app ? app.name : code
        },
        crc: crcModelId,
        // 配网元数据来自字典表，字典管理有改动后重新加载
        loadMeta() {
            return pairingApi.PairingMeta().then((res) => {
                this.meta = res.data.data || {}
            }).catch(() => { })
        },
        categoryName(id) {
            return (this.categories.find((c) => c.id === id) || {}).name || id
        },
        // 型号的适用 App：为空表示所有 App 可用，未在 meta 里的 code 直接显示 code
        appNames(model) {
            return (model.apps || []).map((code) => {
                const app = (this.meta.apps || []).find((a) => a.code === code)
                return (app && app.name) || code
            })
        },
        openTextManager(type) {
            this.$refs.textManager.open(type)
        },
        // 文案库有改动：刷新正在编辑的方案/分类里的文案下拉
        onTextChanged() {
            this.$refs.editor.loadTexts()
            this.$refs.categoryManager.loadTexts()
        },
        openPreview() {
            pairingApi.AppBleTable().then((res) => {
                this.previewData = JSON.stringify(res.data.data, null, 2)
                this.previewCount = Object.keys(res.data.data).length
                this.previewVisible = true
            })
        },
        // 分类分组按当前 App 视图取：传 app 时后端只返回该 App 可用的分类，sort 也是该 App 的排序
        loadCategories() {
            const params = {}
            if (this.appFilter) params.app = this.appFilter
            return pairingApi.PairingCategoryList(params).then((res) => {
                this.categories = res.data.data
                if (!this.appFilter) {
                    // 未按 App 筛选时就是全量分类，表单下拉直接复用
                    this.allCategories = this.categories
                    return
                }
                this.loadAllCategories()
            }).catch(() => { })
        },
        // 表单里的分类下拉不受 App 视图限制
        loadAllCategories() {
            return pairingApi.PairingCategoryList().then((res) => {
                this.allCategories = res.data.data
            }).catch(() => { })
        },
        // 切换 App 视图：分类分组和型号列表都按当前 App 重新取
        // 新建时默认勾选的适用 App
        defaultApps() {
            const code = this.meta.default_app
            return code ? [code] : []
        },
        onAppFilterChange() {
            this.loadCategories().then(() => {
                // 当前选中的分类可能不在新视图里
                if (this.category && !this.categories.some((c) => c.id === this.category)) this.category = ''
                this.loadModels()
            })
        },
        // 分类管理弹窗关闭后刷新：分类名称、顺序、适用 App 可能变了
        onCategoryChanged() {
            this.loadCategories().then(() => {
                if (this.category && !this.categories.some((c) => c.id === this.category)) this.category = ''
                this.loadModels()
            })
        },
        searchModels() {
            if (this.kname === this.lastKname) return
            this.lastKname = this.kname
            this.loadModels()
        },
        // list 为某个分类调整后的顺序
        sortModels(list) {
            const category = list[0].category
            const rest = this.models.filter((m) => m.category !== category)
            this.models = [...rest, ...list]
            const params = { codes: list.map((m) => m.code) }
            // 按 App 视图拖动只写该 App 的顺序，不动基础排序
            if (this.appFilter) params.app = this.appFilter
            pairingApi.PairingModelSort(params).then((res) => {
                if (res.data.code == 200) {
                    this.$message.success(this.appFilter ? '排序已保存（仅当前 App）' : '排序已保存')
                    return
                }
                this.$message.error('排序失败' + res.data.msg)
                this.loadModels()
            })
        },
        schemeRowClass({ rowIndex }) {
            const { from, over } = this.schemeDrag
            if (from === rowIndex) return 'is-dragging'
            if (over === rowIndex && from !== rowIndex) return 'is-drag-over'
            return ''
        },
        // el-table 不支持给行绑定事件，直接在 tbody 上做事件委托
        bindSchemeDrag() {
            const table = this.$refs.schemeTable
            const tbody = table && table.$el.querySelector('.el-table__body-wrapper tbody')
            if (!tbody || tbody.__dragBound) return
            tbody.__dragBound = true
            const rowIndex = (e) => {
                const tr = e.target.closest && e.target.closest('tr')
                return tr ? Array.prototype.indexOf.call(tbody.children, tr) : -1
            }
            tbody.addEventListener('mousedown', (e) => {
                const tr = e.target.closest('tr')
                if (tr) tr.draggable = !!e.target.closest('.drag-handle')
            })
            tbody.addEventListener('dragstart', (e) => {
                this.schemeDrag = { from: rowIndex(e), over: -1 }
                e.dataTransfer.effectAllowed = 'move'
                e.dataTransfer.setData('text/plain', '')
            })
            tbody.addEventListener('dragover', (e) => {
                e.preventDefault()
                if (this.schemeDrag.from > -1) this.schemeDrag = { ...this.schemeDrag, over: rowIndex(e) }
            })
            tbody.addEventListener('drop', (e) => {
                e.preventDefault()
                const { from, over } = this.schemeDrag
                this.schemeDrag = { from: -1, over: -1 }
                if (from < 0 || over < 0 || from === over) return
                const next = [...this.schemes]
                const [moved] = next.splice(from, 1)
                next.splice(over, 0, moved)
                this.sortSchemes(next)
            })
            tbody.addEventListener('dragend', (e) => {
                this.schemeDrag = { from: -1, over: -1 }
                const tr = e.target.closest && e.target.closest('tr')
                if (tr) tr.draggable = false
            })
        },
        sortSchemes(list) {
            this.schemes = list
            pairingApi.PairingSchemeSort({ pairing_model_code: this.current.code, codes: list.map((s) => s.code) }).then((res) => {
                if (res.data.code == 200) {
                    this.$message.success('排序已保存')
                    return
                }
                this.$message.error('排序失败' + res.data.msg)
                this.loadSchemes()
            })
        },
        loadModels() {
            this.modelLoading = true
            const params = { category: this.category, kname: this.kname }
            // 不传 app 表示全部（后端只在传 code 时按 App 过滤）
            if (this.appFilter) params.app = this.appFilter
            return pairingApi.PairingModelList(params).then((res) => {
                this.modelLoading = false
                this.models = res.data.data
                if (this.current) {
                    this.current = this.models.find((m) => m.code === this.current.code) || null
                }
            }).catch(() => {
                this.modelLoading = false
            })
        },
        selectModel(m) {
            this.current = m
            this.loadSchemes()
        },
        loadSchemes() {
            this.schemeLoading = true
            pairingApi.PairingSchemeList({ pairing_model_code: this.current.code }).then((res) => {
                this.schemeLoading = false
                this.schemes = res.data.data
                this.$nextTick(() => this.bindSchemeDrag())
            }).catch(() => {
                this.schemeLoading = false
            })
        },
        // 方案可能保存到了其他配网型号（复制到新型号），保存后切过去方便查看
        onSchemeSaved(pairingModelCode) {
            if (pairingModelCode && this.current && pairingModelCode !== this.current.code) {
                // 目标型号可能被当前分类/搜索/App 筛选过滤掉，先清空筛选
                this.category = ''
                this.kname = ''
                this.lastKname = ''
                this.appFilter = ''
                // App 筛选清空后分类分组也要回到全量视图
                this.loadCategories().then(() => this.loadModels()).then(() => {
                    const target = this.models.find((m) => m.code === pairingModelCode)
                    if (target) this.selectModel(target)
                })
                return
            }
            this.loadSchemes()
            this.loadModels()
        },
        imageOf(scheme) {
            const img = scheme.images.find((i) => i.type === 'left') || scheme.images.find((i) => i.type === 'main')
            return img && img.url
        },
        modelIdOf(scheme) {
            return scheme.model_id
        },
        functionsOf(scheme, type) {
            return (this.meta.functions || []).filter((f) => f.type === type && scheme.functions.includes(f.code))
        },
        openModelDialog(model) {
            this.modelForm = model
                ? {
                    code: model.code,
                    category: model.category,
                    model: model.model,
                    name: model.name === model.model ? '' : model.name,
                    pair_image: model.pair_image || '',
                    ble_table: !!model.ble_table,
                    apps: [...(model.apps || [])],
                    scheme_count: model.scheme_count,
                }
                // 新建默认只给默认 App（meta.default_app，通常是 OSAIO），避免新数据对所有 App 可见
                : { code: '', category: this.category || '', model: '', name: '', pair_image: '', ble_table: false, apps: this.defaultApps(), scheme_count: 0 }
            this.modelDialogVisible = true
            this.$nextTick(() => this.$refs.modelFormRef && this.$refs.modelFormRef.clearValidate())
        },
        saveModel() {
            this.$refs.modelFormRef.validate((valid) => {
                if (!valid) return
                pairingApi.PairingModelSave({
                    code: this.modelForm.code,
                    category: this.modelForm.category,
                    model: this.modelForm.model,
                    model_id: crcModelId(this.modelForm.model),
                    name: this.modelForm.name,
                    pair_image: this.modelForm.pair_image,
                    ble_table: this.modelForm.ble_table,
                    // 空数组也要提交：清空后表示所有 App 可用
                    apps: this.modelForm.apps || [],
                }).then((res) => {
                    if (res.data.code == 200) {
                        this.$message.success('保存成功')
                        this.modelDialogVisible = false
                        this.loadModels()
                        return
                    }
                    this.$message.error('保存失败' + res.data.msg)
                })
            })
        },
        deleteModel(model) {
            this.$confirm(`确定删除配网型号「${model.model}」吗？`, '提示', { type: 'warning' }).then(() => {
                pairingApi.PairingModelDelete({ code: model.code }).then((res) => {
                    if (res.data.code == 200) {
                        this.$message.success('删除成功')
                        this.current = null
                        this.loadModels()
                        return
                    }
                    this.$message.error(res.data.msg)
                })
            }).catch(() => { })
        },
        copyScheme(scheme) {
            const copy = JSON.parse(JSON.stringify(scheme))
            copy.code = ''
            copy.name = scheme.name + ' 副本'
            this.$refs.editor.open(this.current, copy)
        },
        deleteScheme(scheme) {
            this.$confirm(`确定删除配网方案「${scheme.name}」吗？`, '提示', { type: 'warning' }).then(() => {
                pairingApi.PairingSchemeDelete({ code: scheme.code }).then((res) => {
                    if (res.data.code == 200) {
                        this.$message.success('删除成功')
                        this.onSchemeSaved()
                        return
                    }
                    this.$message.error(res.data.msg)
                })
            }).catch(() => { })
        },
    },
}
</script>

<style scoped>
.category-btn {
    margin-left: auto;
}

.pair-image-row {
    display: flex;
    align-items: center;
    gap: 8px;
    width: 100%;
}

.pair-image-thumb {
    width: 32px;
    height: 32px;
    flex-shrink: 0;
    border: 1px solid #e4e7ed;
    border-radius: 3px;
}

.preview-json {
    max-height: 60vh;
    margin: 0;
    padding: 10px;
    overflow: auto;
    font-size: 12px;
    line-height: 18px;
    background: #f5f7fa;
    border-radius: 4px;
}

.page-toolbar {
    display: flex;
    align-items: center;
    gap: 8px;
    margin-bottom: 10px;
}

.pairing-layout {
    display: flex;
    gap: 16px;
    height: calc(100vh - 210px);
    min-height: 480px;
}

.model-panel {
    width: 300px;
    flex-shrink: 0;
    display: flex;
    flex-direction: column;
    border: 1px solid #e4e7ed;
    border-radius: 4px;
}

.panel-toolbar {
    display: flex;
    flex-direction: column;
    gap: 6px;
    padding: 8px;
    border-bottom: 1px solid #e4e7ed;
}

.toolbar-line {
    display: flex;
    gap: 6px;
}

.model-list {
    flex: 1;
    overflow: auto;
    padding: 6px;
}

.model-item {
    padding: 8px 10px;
    margin-bottom: 4px;
    border: 1px solid #e4e7ed;
    border-radius: 3px;
    cursor: pointer;
}

.model-item:hover {
    border-color: #409eff;
}

.sort-tip {
    padding: 4px 8px;
    font-size: 12px;
    line-height: 18px;
    color: #909399;
}

.drag-handle {
    color: #c0c4cc;
    cursor: move;
}

.model-item.is-dragging,
.scheme-panel ::v-deep(.is-dragging) {
    opacity: 0.4;
}

.model-item.is-drag-over {
    border-top: 2px solid #409eff;
}

.scheme-panel ::v-deep(.is-drag-over td) {
    border-top: 2px solid #409eff;
}

.model-group + .model-group {
    margin-top: 8px;
}

.group-title {
    padding: 4px 2px;
    font-size: 12px;
    color: #909399;
}

.model-item.sortable {
    cursor: move;
}

.model-item.active {
    border-color: #409eff;
    background: #ecf5ff;
}

.model-main {
    display: flex;
    align-items: center;
    gap: 6px;
}

.model-name {
    font-size: 13px;
    font-weight: 500;
    color: #303133;
}

.model-sub {
    margin-top: 3px;
    font-size: 12px;
    color: #909399;
}

.model-apps {
    display: flex;
    flex-wrap: wrap;
    gap: 4px;
    margin-top: 4px;
}

.model-apps-all {
    font-size: 12px;
    color: #c0c4cc;
}

.scheme-panel {
    flex: 1;
    min-width: 0;
    overflow: auto;
}

.scheme-header {
    display: flex;
    justify-content: space-between;
    align-items: center;
    margin-bottom: 12px;
}

.scheme-title {
    font-size: 16px;
    font-weight: 500;
    color: #303133;
}

.scheme-meta {
    margin-left: 12px;
    font-size: 12px;
    color: #909399;
}

.scheme-thumb {
    width: 40px;
    height: 40px;
}

.fn-tag {
    margin: 2px 4px 2px 0;
}

.empty {
    padding: 40px 0;
    text-align: center;
    font-size: 13px;
    color: #c0c4cc;
}

.model-id {
    font-family: monospace;
    margin-right: 10px;
}

.form-tip {
    font-size: 12px;
    line-height: 18px;
    color: #909399;
}
</style>
